import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import PageContainer from 'pages/PageContainer';
import { InputPasswordWrapper } from 'components/Input/InputPassword/InputPasswordWrapper';
import { PASSWORD_RECOVERY_MODAL_DATA } from 'shared/constants/modal-constants';
import {
  PASSWORD_RECOVERY_FORM_NAMES,
  PASSWORD_RECOVERY_FORM_LABELS,
} from 'shared/constants/form-constants';
import { FORMS_BUTTON } from 'shared/constants/buttons-constants';
import { urls } from 'shared/constants/routing.constants';
import { useNavigate } from 'react-router-dom';
import { ConfirmPasswordRequest, TError } from 'shared/types/api.interface';
import { useParams } from 'react-router';
import { useResetPasswordMutation } from 'redux/api/auth.api';
import { useLoading } from 'shared/hooks/useLoading';
import Typography from '@mui/material/Typography';
import { EnterNewPasswordFormValidation, TEnterPasswordFormType } from './vatidationSchema';

const fieldsArr = [PASSWORD_RECOVERY_FORM_NAMES.new_password];

export const PasswordRecovery = () => {
  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<TEnterPasswordFormType>({
    defaultValues: {
      [PASSWORD_RECOVERY_FORM_NAMES.new_password]: '',
      [PASSWORD_RECOVERY_FORM_NAMES.confirm_new_password]: '',
    },
    resolver: zodResolver(EnterNewPasswordFormValidation),
  });
  const navigate = useNavigate();
  const { uidb64, token } = useParams();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  useLoading(isLoading);

  const catchSubmitError = (errorsArr: TError[]) => {
    fieldsArr.forEach((field) => {
      setError(field, {
        type: 'custom',
        message: errorsArr.find((item: TError) => item.attr === field)?.detail || '',
      });
    });
  };

  const onSubmit: SubmitHandler<TEnterPasswordFormType> = async (data: ConfirmPasswordRequest) => {
    try {
      const requestData = {
        ...data,
        uidb64,
        token,
      };
      if (uidb64 && token) {
        await resetPassword(requestData).unwrap();
        navigate(urls.commonPages.home);
      }
    } catch (err) {
      catchSubmitError(err?.data?.errors);
    }
  };

  return (
    <PageContainer showMenu={false}>
      <Stack
        alignItems="center"
        width="100%"
        sx={{
          pt: { md: '134px', sm: '40px', xs: '40px' },
          height: {
            lg: 'calc(100vh - 104px - 30px)',
            md: 'calc(100vh - 76px - 30px)',
            xs: 'calc(100vh - 76px - 40px)',
          },
        }}
      >
        <Stack
          alignItems="stretch"
          rowGap="8px"
          justifyContent="center"
          sx={{
            width: '433px',
            '@media (max-width:500px)': {
              width: '100%',
            },
          }}
        >
          <Typography variant="h2" color="primary.main" maxWidth="100%">
            {PASSWORD_RECOVERY_MODAL_DATA.title}
          </Typography>
          <Typography
            variant="subtitle1"
            color="primary.main"
            textAlign="center"
            sx={{ p: { md: '0 0 42px', sm: '0 0 64px', xs: '0 0 64px' } }}
          >
            {PASSWORD_RECOVERY_MODAL_DATA.subtitle}
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              alignItems="stretch"
              width="100%"
              sx={{ rowGap: { lg: '37px', md: '40px', sm: '40px', xs: '40px' } }}
            >
              <InputPasswordWrapper
                name={PASSWORD_RECOVERY_FORM_NAMES.new_password}
                control={control}
                label={PASSWORD_RECOVERY_FORM_LABELS.new_password}
                errorMessage={errors.new_password ? (errors.new_password?.message as string) : ''}
              />
              <InputPasswordWrapper
                name={PASSWORD_RECOVERY_FORM_NAMES.confirm_new_password}
                control={control}
                label={PASSWORD_RECOVERY_FORM_LABELS.confirm_new_password}
                errorMessage={
                  errors.confirm_new_password
                    ? (errors.confirm_new_password?.message as string)
                    : ''
                }
              />
              <Button
                sx={{ mt: { md: '19px', sm: 0, xs: 0 }, alignSelf: 'center' }}
                variant="fixedSize"
                type="submit"
              >
                {FORMS_BUTTON.passwordRecoverySubmitButton}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Stack>
    </PageContainer>
  );
};
